// i18n with stable keys (RF-LANG-04). English is the source; other locales are optional,
// may be partial and fall back to English. User content (PRD text, task titles) is never
// passed through `t`.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { en, type MessageKey } from "./en.ts";
import { ptBR } from "./pt-BR.ts";
import { readLocal, writeLocal } from "../lib/storage.ts";

export type Locale = "en" | "pt-BR";
export const LOCALES: { id: Locale; label: string }[] = [
  { id: "en", label: "English" },
  { id: "pt-BR", label: "Português (Brasil)" },
];

const catalogs: Record<Locale, Partial<Record<MessageKey, string>>> = { en, "pt-BR": ptBR };

export type Params = Record<string, string | number>;
export type Translate = (key: MessageKey, params?: Params) => string;

export function format(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => (name in params ? String(params[name]) : m));
}

export function translate(locale: Locale, key: MessageKey, params?: Params): string {
  const template = catalogs[locale][key] ?? en[key] ?? key;
  return format(template, params);
}

interface I18nValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
  formatTime: (iso: string | null | undefined) => string;
  formatDateTime: (iso: string | null | undefined) => string;
  formatRelative: (iso: string | null | undefined) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = readLocal<string>("locale", "en");
    return stored === "pt-BR" ? "pt-BR" : "en";
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    writeLocal("locale", l);
  }, []);

  const value = useMemo<I18nValue>(() => {
    const t: Translate = (key, params) => translate(locale, key, params);
    const timeFmt = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
    const dateTimeFmt = new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" });
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    const valid = (iso: string | null | undefined): Date | null => {
      if (!iso) return null;
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? null : d;
    };
    return {
      locale,
      setLocale,
      t,
      formatTime: (iso) => {
        const d = valid(iso);
        return d ? timeFmt.format(d) : "—";
      },
      formatDateTime: (iso) => {
        const d = valid(iso);
        return d ? dateTimeFmt.format(d) : "—";
      },
      formatRelative: (iso) => {
        const d = valid(iso);
        if (!d) return "—";
        const diff = (d.getTime() - Date.now()) / 1000;
        const abs = Math.abs(diff);
        if (abs < 45) return rtf.format(Math.round(diff), "second");
        if (abs < 2700) return rtf.format(Math.round(diff / 60), "minute");
        if (abs < 79200) return rtf.format(Math.round(diff / 3600), "hour");
        if (abs < 2592000) return rtf.format(Math.round(diff / 86400), "day");
        return dateTimeFmt.format(d);
      },
    };
  }, [locale, setLocale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export function useT(): Translate {
  return useI18n().t;
}

export type { MessageKey };
